import { CSSProperties } from 'react';
import EmojiPicker, { EmojiClickData, Theme, EmojiStyle, Categories } from 'emoji-picker-react';
import { useTranslation } from 'react-i18next';

/**
 * EmojiPickerCompact
 *
 * Shared compact emoji picker wrapper with WhatsApp-style density.
 * Smaller emojis, tighter grid, emoji category tabs, no focus rings.
 *
 * Usage:
 * <EmojiPickerCompact onEmojiClick={handler} width={320} height={350} />
 */

interface EmojiPickerCompactProps {
  onEmojiClick: (emojiData: EmojiClickData) => void;
  width?: number;
  height?: number;
  searchPlaceholder?: string;
}

/** WhatsApp-style compact CSS overrides */
const compactStyle: CSSProperties = {
  // Emoji grid
  '--epr-emoji-size': '24px',
  '--epr-emoji-padding': '4px',
  '--epr-emoji-fullsize': '32px',

  // Category nav tabs
  '--epr-category-navigation-button-size': '28px',

  // Category labels (e.g. "Smileys & People")
  '--epr-category-label-height': '28px',
  '--epr-category-label-padding': '0 12px',

  // Search bar
  '--epr-search-input-height': '32px',
  '--epr-search-bar-inner-padding': '6px 10px',
  '--epr-search-input-border-radius': '10px',
  '--epr-search-border-color': '#e5e7eb',
  '--epr-search-border-color-active': '#9ca3af',
  '--epr-search-input-bg-color': '#f9fafb',
  '--epr-search-input-bg-color-active': '#ffffff',
  '--epr-search-input-placeholder-color': '#9ca3af',
  '--epr-search-input-text-color': '#374151',
  '--epr-search-input-padding': '0 10px',

  // General spacing
  '--epr-header-padding': '8px 10px 0',
  '--epr-horizontal-padding': '8px',
  '--epr-category-padding': '0 8px',

  // Border radius
  '--epr-picker-border-radius': '12px',
} as CSSProperties;

/** Category order */
const CATEGORIES = [
  { category: Categories.SUGGESTED, name: 'Recent' },
  { category: Categories.SMILEYS_PEOPLE, name: 'Smileys' },
  { category: Categories.ANIMALS_NATURE, name: 'Animals' },
  { category: Categories.FOOD_DRINK, name: 'Food' },
  { category: Categories.TRAVEL_PLACES, name: 'Travel' },
  { category: Categories.ACTIVITIES, name: 'Activities' },
  { category: Categories.OBJECTS, name: 'Objects' },
  { category: Categories.SYMBOLS, name: 'Symbols' },
  { category: Categories.FLAGS, name: 'Flags' },
];

/**
 * CSS overrides for compact WhatsApp-style picker
 */
const CSS_OVERRIDES = `
  .epr-emoji-compact .epr-cat-btn::before {
    display: none !important;
  }
  .epr-emoji-compact .epr-cat-btn.epr-active {
    outline: none !important;
    border-bottom: 2px solid #6b7280;
    border-radius: 0;
  }
  .epr-emoji-compact .epr-cat-btn {
    background-image: none !important;
    font-size: 18px;
    line-height: 28px;
    text-align: center;
    border-bottom: 2px solid transparent;
    transition: border-color 0.15s;
  }
  .epr-emoji-compact .epr-icn-suggested::after { content: "\\1F552"; }
  .epr-emoji-compact .epr-icn-smileys_people::after { content: "\\1F600"; }
  .epr-emoji-compact .epr-icn-animals_nature::after { content: "\\1F43E"; }
  .epr-emoji-compact .epr-icn-food_drink::after { content: "\\1F354"; }
  .epr-emoji-compact .epr-icn-travel_places::after { content: "\\2708\\FE0F"; }
  .epr-emoji-compact .epr-icn-activities::after { content: "\\26BD"; }
  .epr-emoji-compact .epr-icn-objects::after { content: "\\1F4A1"; }
  .epr-emoji-compact .epr-icn-symbols::after { content: "\\2764\\FE0F"; }
  .epr-emoji-compact .epr-icn-flags::after { content: "\\1F3C1"; }

  /* Space below category nav tabs */
  .epr-emoji-compact .epr-category-nav {
    padding-bottom: 6px !important;
    border-bottom: 1px solid #f3f4f6;
    margin-bottom: 6px;
  }

  /* Category labels: our font, smaller size, consistent spacing */
  .epr-emoji-compact .epr-emoji-category-label {
    font-family: 'DM Sans', sans-serif !important;
    font-size: 11px !important;
    font-weight: 600 !important;
    text-transform: uppercase !important;
    letter-spacing: 0.04em !important;
    color: #9ca3af !important;
    margin-top: 6px !important;
    margin-bottom: 6px !important;
  }

  /* Search input: remove blue outline, clean focus state, fix icon overlap */
  .epr-emoji-compact input[type="text"] {
    font-size: 13px !important;
    padding-left: 30px !important;
    outline: none !important;
    box-shadow: none !important;
    transition: border-color 0.15s, background-color 0.15s;
  }
  .epr-emoji-compact .epr-search > div {
    position: relative;
  }
  .epr-emoji-compact .epr-icn-search {
    left: 10px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
    width: 14px !important;
    height: 14px !important;
    background-size: 14px !important;
  }
  .epr-emoji-compact input[type="text"]:focus {
    border-color: #9ca3af !important;
    outline: none !important;
    box-shadow: none !important;
  }
  /* Clean up the search clear button */
  .epr-emoji-compact .epr-btn-clear-search {
    opacity: 0.4;
    transition: opacity 0.15s;
  }
  .epr-emoji-compact .epr-btn-clear-search:hover {
    opacity: 0.7;
  }
`;

/** Must match --epr-emoji-fullsize / --epr-horizontal-padding in compactStyle. */
const EMOJI_CELL_PX = 32;
const HORIZONTAL_PADDING_PX = 8;

/**
 * The picker derives its column count from the raw width but also adds the horizontal
 * padding, so a non-aligned width pushes the last column out of view. Snap the width to
 * whole columns plus padding on both sides.
 */
const snapPickerWidth = (width: number): number => {
  const columns = Math.max(1, Math.floor((width - 2 * HORIZONTAL_PADDING_PX) / EMOJI_CELL_PX));
  return columns * EMOJI_CELL_PX + 2 * HORIZONTAL_PADDING_PX;
};

export const EmojiPickerCompact = ({
  onEmojiClick,
  width = 320,
  height = 350,
  searchPlaceholder,
}: EmojiPickerCompactProps) => {
  const { t } = useTranslation('common');

  return (
    <div onWheel={(e) => e.stopPropagation()} className="overscroll-contain epr-emoji-compact">
      <style>{CSS_OVERRIDES}</style>
      <EmojiPicker
        onEmojiClick={onEmojiClick}
        width={snapPickerWidth(width)}
        height={height}
        theme={Theme.LIGHT}
        emojiStyle={EmojiStyle.NATIVE}
        searchPlaceHolder={searchPlaceholder || t('emoji_search_placeholder')}
        previewConfig={{ showPreview: false }}
        skinTonesDisabled
        lazyLoadEmojis
        categories={CATEGORIES}
        style={compactStyle}
      />
    </div>
  );
};
